import { forwardRef } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { spacing32, grey3, backgroundColorLevelCurrent, setAppearanceConstantLocal } from "@churchofjesuschrist/eden-style-constants";
import { useHasOverflow } from "@churchofjesuschrist/eden-has-overflow";
import { deepMapChildren, mergeRefs } from "@churchofjesuschrist/eden-utilities";
import { OverflowContext } from "../utils/context";
import Scrollable from "./Scrollable";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const [fadeToColorDef, fadeToColor] = setAppearanceConstantLocal("fade-to-color", {
  light: grey3,
  dark: backgroundColorLevelCurrent,
  prefix: "eden"
});
const Container = styled.div.withConfig({
  componentId: "sc-3v5koa-0"
})(["position:relative;display:flex;flex-direction:column;align-items:stretch;overflow:hidden;"]);
const Mask = styled.span.withConfig({
  componentId: "sc-3v5koa-1"
})(["", ";position:absolute;top:0;left:0;right:0;bottom:0;pointer-events:none;z-index:2;"], fadeToColorDef);
export const backgroundDef = fadeToColorDef;
export const size = spacing32;
const getBackgroundGradient = dir => `linear-gradient(to ${dir}, transparent calc(100% - ${size}), ${fadeToColor()} 100%)`;
export const topBackground = getBackgroundGradient("top");
export const rightBackground = getBackgroundGradient("right");
export const bottomBackground = getBackgroundGradient("bottom");
export const leftBackground = getBackgroundGradient("left");

/** Automatically handles the fading of overflowing content. */
const Fade = /*#__PURE__*/forwardRef((_ref, forwardedRef) => {
  let {
    children,
    renderAs,
    disableTop,
    disableBottom,
    ...props
  } = _ref;
  const [overflowRef, {
    hasTop,
    hasBottom,
    hasRight,
    hasLeft
  }] = useHasOverflow();
  let hasScrollable = false;
  deepMapChildren(children, child => {
    var _child$type;
    if ((child === null || child === void 0 ? void 0 : child.type) === Scrollable || (child === null || child === void 0 ? void 0 : (_child$type = child.type) === null || _child$type === void 0 ? void 0 : _child$type.target) === Scrollable) {
      hasScrollable = true;
    }
  });
  const getBackground = () => ({
    background: [hasTop && !disableTop ? topBackground : "", hasRight ? rightBackground : "", hasBottom && !disableBottom ? bottomBackground : "", hasLeft ? leftBackground : ""].filter(background => !!background).join(",")
  });
  return /*#__PURE__*/_jsx(Container, {
    as: renderAs,
    ref: hasScrollable ? null : mergeRefs(overflowRef, forwardedRef),
    ...props,
    children: /*#__PURE__*/_jsxs(OverflowContext.Provider, {
      value: overflowRef,
      children: [/*#__PURE__*/_jsx(Mask, {
        style: getBackground()
      }), children]
    })
  });
});
Fade.displayName = "Fade";
Fade.propTypes = {
  /** The HTML tag string (e.g., `"div"`) or React component to render as. */
  renderAs: PropTypes.elementType,
  /** The content to fade. */
  children: PropTypes.node,
  /** Disable top gradient fade. */
  disableTop: PropTypes.bool,
  /** Disable bottom gradient fade. */
  disableBottom: PropTypes.bool
};
export default Fade;